import React, { useEffect, useState, useCallback } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Tooltip,
  makeStyles
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import { toast } from "react-toastify";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";
import copyToClipboard from "../../utils/copyToClipboard";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), margin: theme.spacing(2) },
  filters: { display: "flex", gap: theme.spacing(2), marginBottom: theme.spacing(2) }
}));

const emptyForm = {
  name: "",
  provider: "mercadopago",
  sandbox: true,
  active: true,
  defaultPaymentMethod: "pix",
  financeAccountId: "",
  notes: "",
  credentials: {}
};

// Mapeia provider -> campos de credencial necessarios. Quando outros
// gateways forem adicionados ao backend (PagSeguro, Cielo, etc.), basta
// estender este mapa.
const PROVIDER_FIELDS = {
  mercadopago: [
    { key: "accessToken", label: "Access Token", required: true }
  ],
  asaas: [
    { key: "apiKey", label: "API Key", required: true }
  ],
  stripe: [
    { key: "secretKey", label: "Secret Key (sk_live_ ou sk_test_)", required: true }
  ],
  pagarme: [
    { key: "apiKey", label: "API Key (ak_live_ ou ak_test_)", required: true }
  ]
};

const PROVIDER_LABELS = {
  mercadopago: "Mercado Pago",
  asaas: "Asaas",
  stripe: "Stripe",
  pagarme: "Pagar.me"
};

const METHOD_LABELS = {
  pix: "PIX",
  boleto: "Boleto",
  credit_card: "Cartão de crédito",
  debit_card: "Cartão de débito"
};

const emptyFeeForm = {
  paymentMethod: "pix",
  installments: 1,
  fixedFee: 0,
  percentageFee: 0,
  settlementDays: 0,
  isActive: true
};

const PaymentGatewaysPage = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dialog, setDialog] = useState({ open: false, form: emptyForm, editing: null });
  const [feesDialog, setFeesDialog] = useState({ open: false, gateway: null, rows: [], form: emptyFeeForm, editingId: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payment-gateways");
      setRows(data || []);
    } catch {
      toast.error("Erro ao carregar gateways");
    }
  }, []);

  useEffect(() => {
    api.get("/payment-gateways/providers").then((r) => setProviders(r.data || [])).catch(() => {});
    api.get("/finance/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    load();
  }, [load]);

  const open = (record = null) => {
    if (record) {
      setDialog({
        open: true,
        editing: record,
        form: {
          ...emptyForm,
          ...record,
          financeAccountId: record.financeAccountId ?? "",
          credentials: {} // sempre limpa: ciphertext nunca volta
        }
      });
    } else {
      setDialog({ open: true, editing: null, form: emptyForm });
    }
  };
  const close = () => setDialog((d) => ({ ...d, open: false }));

  const save = async () => {
    try {
      const payload = { ...dialog.form };
      if (payload.financeAccountId === "") payload.financeAccountId = null;
      // Se editando e não preencheu credenciais, não enviamos para preservar.
      if (
        dialog.editing &&
        (!payload.credentials || Object.values(payload.credentials).every((v) => !v))
      ) {
        delete payload.credentials;
      }
      if (dialog.editing) {
        await api.put(`/payment-gateways/${dialog.editing.id}`, payload);
        toast.success("Atualizado");
      } else {
        await api.post("/payment-gateways", payload);
        toast.success("Criado");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar");
    }
  };

  const remove = async (r) => {
    if (!(await confirm({ title: `Excluir o gateway "${r.name}"?`, message: "Cobranças já geradas continuam existindo, mas novas não poderão ser criadas por ele." }))) return;
    try {
      await api.delete(`/payment-gateways/${r.id}`);
      toast.success("Removido");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const rotate = async (r) => {
    if (!(await confirm({ title: "Rotacionar o segredo do webhook?", message: "O segredo atual para de validar na hora. Atualize também no painel do gateway, senão as notificações passam a ser recusadas." }))) return;
    try {
      const { data } = await api.post(`/payment-gateways/${r.id}/rotate-secret`);
      toast.success("Novo secret gerado");
      copyToClipboard(data.webhookSecret);
      load();
    } catch {
      toast.error("Erro ao rotacionar");
    }
  };

  const copyWebhook = (r) => {
    const base = window.location.origin.replace(/:\d+$/, "") + ":8080";
    const url = `${base}/webhooks/payment/${r.provider}/${r.id}`;
    copyToClipboard(url);
    toast.info("URL copiada");
  };

  /* =========================================================
   * Gerenciamento de taxas (PaymentProviderFees) por gateway
   * ========================================================= */
  const openFees = async (gw) => {
    try {
      const { data } = await api.get(`/payment-provider-fees?providerId=${gw.id}`);
      setFeesDialog({
        open: true,
        gateway: gw,
        rows: data || [],
        form: emptyFeeForm,
        editingId: null
      });
    } catch {
      toast.error("Erro ao carregar taxas");
    }
  };

  const closeFees = () => setFeesDialog((f) => ({ ...f, open: false }));

  const loadFees = async (gwId) => {
    try {
      const { data } = await api.get(`/payment-provider-fees?providerId=${gwId}`);
      setFeesDialog((f) => ({ ...f, rows: data || [] }));
    } catch {
      toast.error("Erro ao recarregar taxas");
    }
  };

  const saveFee = async () => {
    const gw = feesDialog.gateway;
    if (!gw) return;
    const payload = {
      providerId: gw.id,
      paymentMethod: feesDialog.form.paymentMethod,
      installments: Number(feesDialog.form.installments) || 1,
      fixedFee: Number(feesDialog.form.fixedFee) || 0,
      percentageFee: Number(feesDialog.form.percentageFee) || 0,
      settlementDays: Number(feesDialog.form.settlementDays) || 0,
      isActive: !!feesDialog.form.isActive
    };
    try {
      await api.post("/payment-provider-fees", payload);
      toast.success("Taxa salva");
      setFeesDialog((f) => ({ ...f, form: emptyFeeForm, editingId: null }));
      loadFees(gw.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar taxa");
    }
  };

  const editFee = (fee) => {
    setFeesDialog((f) => ({
      ...f,
      form: {
        paymentMethod: fee.paymentMethod,
        installments: fee.installments,
        fixedFee: fee.fixedFee,
        percentageFee: fee.percentageFee,
        settlementDays: fee.settlementDays,
        isActive: !!fee.isActive
      },
      editingId: fee.id
    }));
  };

  const deleteFee = async (fee) => {
    if (!(await confirm({ title: `Excluir a taxa de ${fee.paymentMethod} em ${fee.installments}×?`, message: "As simulações passam a usar a taxa padrão do provedor." }))) return;
    try {
      await api.delete(`/payment-provider-fees/${fee.id}`);
      toast.success("Taxa removida");
      loadFees(feesDialog.gateway.id);
    } catch {
      toast.error("Erro ao remover taxa");
    }
  };

  const fields = PROVIDER_FIELDS[dialog.form.provider] || [];

  return (
    <Paper className={classes.root}>
      {confirmDialog}
      <h2>Gateways de Pagamento</h2>
      <div className={classes.filters}>
        <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => open(null)}>
          Novo
        </Button>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nome</TableCell>
            <TableCell>Provedor</TableCell>
            <TableCell>Modo</TableCell>
            <TableCell>Conta Financeira</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.name}</TableCell>
              <TableCell>{PROVIDER_LABELS[r.provider] || r.provider}</TableCell>
              <TableCell>
                <Chip size="small" label={r.sandbox ? "Sandbox" : "Produção"} color={r.sandbox ? "default" : "primary"} />
              </TableCell>
              <TableCell>
                {accounts.find((a) => a.id === r.financeAccountId)?.name || "-"}
              </TableCell>
              <TableCell>
                <Chip size="small" label={r.active ? "Ativo" : "Inativo"} color={r.active ? "primary" : "default"} />
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Gerenciar taxas do provider">
                  <IconButton size="small" onClick={() => openFees(r)}><MonetizationOnIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Copiar URL do webhook">
                  <IconButton size="small" onClick={() => copyWebhook(r)}><FileCopyIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Rotacionar webhook secret">
                  <IconButton size="small" onClick={() => rotate(r)}><VpnKeyIcon fontSize="small" /></IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => open(r)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={6} align="center">Nenhum gateway.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={dialog.open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.editing ? "Editar gateway" : "Novo gateway"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" variant="outlined" label="Nome *"
                value={dialog.form.name}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, name: e.target.value } })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth select size="small" variant="outlined" label="Provedor *"
                value={dialog.form.provider}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, provider: e.target.value, credentials: {} } })}>
                {(providers.length ? providers : ["mercadopago", "asaas", "stripe", "pagarme"]).map((p) => (
                  <MenuItem key={p} value={p}>{PROVIDER_LABELS[p] || p}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth select size="small" variant="outlined" label="Modo"
                value={dialog.form.sandbox ? "1" : "0"}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, sandbox: e.target.value === "1" } })}>
                <MenuItem value="1">Sandbox</MenuItem>
                <MenuItem value="0">Produção</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select size="small" variant="outlined" label="Forma padrão"
                value={dialog.form.defaultPaymentMethod || ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, defaultPaymentMethod: e.target.value } })}>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="boleto">Boleto</MenuItem>
                <MenuItem value="credit_card">Cartão crédito</MenuItem>
                <MenuItem value="debit_card">Cartão débito</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select size="small" variant="outlined" label="Conta Financeira"
                value={dialog.form.financeAccountId ?? ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, financeAccountId: e.target.value } })}>
                <MenuItem value="">—</MenuItem>
                {accounts.map((a) => (<MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select size="small" variant="outlined" label="Ativo"
                value={dialog.form.active ? "1" : "0"}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, active: e.target.value === "1" } })}>
                <MenuItem value="1">Sim</MenuItem>
                <MenuItem value="0">Não</MenuItem>
              </TextField>
            </Grid>

            {fields.map((f) => (
              <Grid item xs={12} key={f.key}>
                <TextField fullWidth size="small" variant="outlined" type="password" autoComplete="new-password"
                  label={`${f.label}${dialog.editing ? " (deixe vazio para manter)" : " *"}`}
                  value={(dialog.form.credentials || {})[f.key] || ""}
                  onChange={(e) =>
                    setDialog({
                      ...dialog,
                      form: { ...dialog.form, credentials: { ...(dialog.form.credentials || {}), [f.key]: e.target.value } }
                    })
                  } />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} size="small" variant="outlined" label="Notas"
                value={dialog.form.notes || ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, notes: e.target.value } })} />
            </Grid>

            {dialog.editing && (
              <Grid item xs={12}>
                <small>
                  Webhook URL:&nbsp;
                  <code>{`${window.location.origin}/webhooks/payment/${dialog.form.provider}/${dialog.editing.id}`}</code>
                </small>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de taxas (PaymentProviderFees) por gateway */}
      <Dialog open={feesDialog.open} onClose={closeFees} maxWidth="md" fullWidth>
        <DialogTitle>
          Taxas do gateway: {feesDialog.gateway?.name}
        </DialogTitle>
        <DialogContent>
          <p style={{ marginTop: 0, color: "#666", fontSize: 13 }}>
            Configure as taxas reais negociadas com este gateway. Sem regras
            cadastradas, o sistema usa defaults conservadores na simulação.
          </p>
          <Grid container spacing={2} alignItems="center" style={{ marginBottom: 12 }}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth select size="small" variant="outlined" label="Método"
                value={feesDialog.form.paymentMethod}
                onChange={(e) => setFeesDialog((f) => ({
                  ...f,
                  form: { ...f.form, paymentMethod: e.target.value, installments: e.target.value === "credit_card" ? f.form.installments : 1 }
                }))}>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="boleto">Boleto</MenuItem>
                <MenuItem value="credit_card">Cartão crédito</MenuItem>
                <MenuItem value="debit_card">Cartão débito</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" variant="outlined" label="Parcelas"
                value={feesDialog.form.installments}
                onChange={(e) => setFeesDialog((f) => ({ ...f, form: { ...f.form, installments: e.target.value } }))}
                inputProps={{ min: 1, max: 12 }}
                disabled={feesDialog.form.paymentMethod !== "credit_card"} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" variant="outlined" label="Taxa fixa (R$)"
                value={feesDialog.form.fixedFee}
                onChange={(e) => setFeesDialog((f) => ({ ...f, form: { ...f.form, fixedFee: e.target.value } }))}
                inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" variant="outlined" label="Taxa % (0–1)"
                value={feesDialog.form.percentageFee}
                onChange={(e) => setFeesDialog((f) => ({ ...f, form: { ...f.form, percentageFee: e.target.value } }))}
                inputProps={{ min: 0, max: 1, step: 0.0001 }}
                helperText="Ex: 0.0299 = 2.99%" />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" variant="outlined" label="Liq. (dias)"
                value={feesDialog.form.settlementDays}
                onChange={(e) => setFeesDialog((f) => ({ ...f, form: { ...f.form, settlementDays: e.target.value } }))}
                inputProps={{ min: 0, max: 365 }} />
            </Grid>
            <Grid item xs={12} sm={1}>
              <Button fullWidth color="primary" variant="contained" onClick={saveFee}>
                {feesDialog.editingId ? "Atualizar" : "Adicionar"}
              </Button>
            </Grid>
          </Grid>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Método</TableCell>
                <TableCell>Parcelas</TableCell>
                <TableCell align="right">Taxa fixa</TableCell>
                <TableCell align="right">Taxa %</TableCell>
                <TableCell align="right">Liquidação</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feesDialog.rows.map((fee) => (
                <TableRow key={fee.id}>
                  <TableCell>{METHOD_LABELS[fee.paymentMethod] || fee.paymentMethod}</TableCell>
                  <TableCell>{fee.installments}×</TableCell>
                  <TableCell align="right">
                    {Number(fee.fixedFee).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                  </TableCell>
                  <TableCell align="right">{(Number(fee.percentageFee) * 100).toFixed(2)}%</TableCell>
                  <TableCell align="right">{fee.settlementDays} dia(s)</TableCell>
                  <TableCell>
                    <Chip size="small" label={fee.isActive ? "Ativa" : "Inativa"} color={fee.isActive ? "primary" : "default"} />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => editFee(fee)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => deleteFee(fee)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {feesDialog.rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">Nenhuma taxa cadastrada — usando defaults conservadores.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFees}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PaymentGatewaysPage;
